import React, { Fragment } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { CorePageParams } from 'site-modules/shared/utils/core-page/params';
import { VehicleEntities } from 'client/data/models/vehicle-v2';
import { InventoryTrimSummaryEntities } from 'client/data/models/inventory-core-configurations';
import { srpLinkBuilder } from 'site-modules/shared/utils/srp-link-constructor';
import { getBuildAndPriceLink } from 'site-modules/shared/utils/build-and-price-link-constructor';
import { getPriceBarSettings, getPriceBarWidth } from 'site-modules/shared/utils/core-page/pricing-bars';
import { getPriceStringV2 } from 'site-modules/shared/utils/price-utils';
import { getSuggestedDisplayPrice } from 'site-modules/shared/utils/core-page/suggested-price';
import { INVENTORY_TYPES_LOWERCASE } from 'client/constants/inventory-types';
import { Link } from 'site-modules/shared/components/link/link';
import { ScrollElement } from 'site-modules/shared/components/scroll-link/scroll-element';
import { PricingAd } from 'site-modules/shared/components/native-ad/pricing-ad/pricing-ad';

export const MAX_TRIMS_COUNT = 7;
export const BAR_HEIGHT = 32;
export const CARD_HEIGHT = 100;
const PRICING_AD_INDEX = 2;

function getPriceLabel({ nationalBasePriceTmv, suggestedPrice }) {
  if (nationalBasePriceTmv) {
    return 'National Market Price';
  }

  return suggestedPrice ? 'Suggested price' : 'MSRP';
}

export function TrimBarsList({
  params,
  vehicle,
  submodelIdentifier = null,
  trimSummaries,
  trimsFeatures = null,
  recommendedTrim = null,
  barClassName = 'bg-blue-90',
  displayLeadForm,
  isMobile = false,
  pricingAdPos = null,
  isInternational = false,
  hidePricingAd = false,
  isSeot5678Enabled = false,
}) {
  const isCore7836Enabled = !!trimsFeatures;
  const priceBarSettings = getPriceBarSettings(trimSummaries);
  const pricingAdIndex = Math.min(PRICING_AD_INDEX, trimSummaries.length - 1);

  return (
    <ul className="trim-bars-list list-unstyled ps-0">
      {trimSummaries.map(
        (
          {
            id,
            trim,
            trimSlug,
            suggestedPrice,
            totalMsrp,
            baseMsrp,
            nationalBasePriceTmv,
            isMostPopular,
            hasLeadFormDealers,
            inventoryCount,
          },
          index
        ) => {
          const ctaId = `cta-${id}`;
          const priceId = `price-${id}`;
          const displayEspPrice = getSuggestedDisplayPrice({
            nationalBasePriceTmv,
            suggestedPrice,
            totalMsrp,
            baseMsrp,
          });
          const showLeadForm = hasLeadFormDealers || isInternational;
          const CardTag = showLeadForm ? 'button' : Link;
          const cardCta = showLeadForm ? 'Compare dealer prices' : 'What should I pay';
          const isRecommended = trimSlug === recommendedTrim;
          const trimFeatures = trimsFeatures?.[trimSlug];
          const accessiblePriceLabel = `${vehicle.year} ${vehicle.make.name} ${vehicle.model.name} ${trim} ${getPriceLabel(
            { nationalBasePriceTmv, suggestedPrice }
          ).toLowerCase()}: `;

          const cardProps = showLeadForm
            ? {
                'data-trim': trim,
                'data-tracking-id': 'view_pricing_bar_details',
                onClick: displayLeadForm,
              }
            : {
                to: inventoryCount
                  ? srpLinkBuilder({
                      make: params.make,
                      model: params.model,
                      submodel: params.submodel,
                      year: params.year,
                      inventorytype: INVENTORY_TYPES_LOWERCASE.NEW,
                      submodelid: params.submodel && submodelIdentifier,
                      trim: trimSlug,
                    })
                  : getBuildAndPriceLink(params),
                'data-tracking-id': inventoryCount
                  ? 'view_pricing_bar_srp_details'
                  : 'view_pricing_bar_configure_details',
                rel: 'nofollow',
              };

          const ctaProps = showLeadForm
            ? {
                'data-tracking-id': 'pricing_bar_text_link',
              }
            : {
                'data-tracking-id': inventoryCount ? 'pricing_bar_text_srp_link' : 'pricing_bar_text_configure_link',
              };

          const trimLabel = !!(isMostPopular || isRecommended) && (isRecommended ? "Editors' Pick" : 'Most Popular');

          return (
            <Fragment key={id}>
              {!hidePricingAd && index === pricingAdIndex && (
                <li className="mb-1">
                  <PricingAd
                    mobile={isMobile}
                    position={pricingAdPos || undefined}
                    showHorizontalLayout
                    showOnlySiteServed
                  />
                </li>
              )}
              <li className="medium mb-1">
                {isMobile && index === MAX_TRIMS_COUNT - 2 && <ScrollElement id="scrollTop-pricing" />}
                <CardTag
                  {...cardProps}
                  className="card-wrapper d-block w-100 border-0 text-decoration-none text-start rounded bg-white px-0_75 py-0_5"
                  aria-labelledby={`${ctaId} ${priceId}`}
                  data-tracking-value={`${index + 1} - ${trimSummaries.length}`}
                >
                  {isSeot5678Enabled ? (
                    <dl className="mb-0_25">
                      <dt className="mb-0_25 text-gray-darker fw-bold d-flex align-items-baseline">
                        <h3 className="mb-0_25 text-gray-darker fw-bold d-flex align-items-baseline">
                          {trim}{' '}
                          {!!trimLabel && (
                            <span className="text-success ms-1 small fw-bold" data-tracking-id="click_trim_label">
                              {trimLabel}
                            </span>
                          )}
                        </h3>
                      </dt>
                      <dd
                        className={classnames(
                          'd-block bar-wrapper w-100 pos-r rounded overlay-white-40 overflow-hidden fw-medium border-0 mb-0_5',
                          barClassName
                        )}
                        style={{ height: `${BAR_HEIGHT}px` }}
                      >
                        <div
                          id={priceId}
                          className="bar pos-a top-0 left-0 h-100 bg-blue-10"
                          style={{
                            width: `${getPriceBarWidth({ priceBarSettings, suggestedPrice: displayEspPrice })}%`,
                          }}
                        >
                          <div className="pos-a center-y right-0 me-1 text-white">
                            <span className="visually-hidden">{accessiblePriceLabel}</span>
                            {getPriceStringV2(displayEspPrice)}
                          </div>
                        </div>
                      </dd>
                    </dl>
                  ) : (
                    <Fragment>
                      <div className="mb-0_25 text-gray-darker fw-bold d-flex align-items-baseline">
                        {trim}{' '}
                        {!!trimLabel && (
                          <div className="text-success ms-1 small fw-bold" data-tracking-id="click_trim_label">
                            {trimLabel}
                          </div>
                        )}
                      </div>
                      <div
                        className={classnames(
                          'd-block bar-wrapper w-100 pos-r rounded overlay-white-40 overflow-hidden fw-medium border-0 mb-0_5',
                          barClassName
                        )}
                        style={{ height: `${BAR_HEIGHT}px` }}
                      >
                        <div
                          id={priceId}
                          className="bar pos-a top-0 left-0 h-100 bg-blue-10"
                          style={{
                            width: `${getPriceBarWidth({ priceBarSettings, suggestedPrice: displayEspPrice })}%`,
                          }}
                        >
                          <div className="pos-a center-y right-0 me-1 text-white">
                            <span className="visually-hidden">
                              {suggestedPrice ? 'Suggested price' : 'MSRP'}:&nbsp;
                            </span>
                            {getPriceStringV2(displayEspPrice)}
                          </div>
                        </div>
                      </div>
                    </Fragment>
                  )}
                  <div
                    className={classnames({
                      'd-flex flex-row align-items-start gap-0_5 pt-md-0_25': isCore7836Enabled,
                    })}
                  >
                    {isCore7836Enabled && (
                      <div className="trim-features flex-grow-1 flex-shrink-1 text-start">
                        {!!trimFeatures && <span className="size-14 text-cool-gray-40">{trimFeatures}</span>}
                      </div>
                    )}
                    <div
                      {...ctaProps}
                      id={ctaId}
                      className={classnames('cta-text text-primary-darker text-end', {
                        'flex-shrink-0 text-nowrap': isCore7836Enabled,
                      })}
                      data-tracking-value={`${index + 1} - ${trimSummaries.length} - ${cardCta}`}
                    >
                      {cardCta}
                      <span className="visually-hidden">&nbsp;for {trim} trim</span>
                      <i className="icon-arrow-right3 size-10 d-inline-block ms-0_25" aria-hidden />
                    </div>
                  </div>
                </CardTag>
              </li>
            </Fragment>
          );
        }
      )}
    </ul>
  );
}

TrimBarsList.propTypes = {
  params: CorePageParams.isRequired,
  vehicle: VehicleEntities.MakeModelSubmodelYear.isRequired,
  trimSummaries: InventoryTrimSummaryEntities.TrimSummaries.isRequired,
  trimsFeatures: PropTypes.objectOf(PropTypes.string),
  displayLeadForm: PropTypes.func.isRequired,
  recommendedTrim: PropTypes.string,
  submodelIdentifier: PropTypes.string,
  className: PropTypes.string,
  barClassName: PropTypes.string,
  isMobile: PropTypes.bool,
  isInternational: PropTypes.bool,
  pricingAdPos: PropTypes.string,
  hidePricingAd: PropTypes.bool,
  isSeot5678Enabled: PropTypes.bool,
};
